import React, { useState, useEffect } from 'react';
import { staffApi } from '../services/api';
import toast from 'react-hot-toast';
import { HiOutlinePlus, HiOutlinePencil, HiOutlineTrash, HiX } from 'react-icons/hi';
import { formatValidationErrors } from '../utils/errorHandler';

const SalaryIncrements = ({ userId, staffName, onIncrementAdded, onCloseModal }) => {
  const [increments, setIncrements] = useState([]);
  const [totalIncrement, setTotalIncrement] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedIncrement, setSelectedIncrement] = useState(null);
  const [formData, setFormData] = useState({
    increment_month: new Date().getMonth() + 1,
    increment_year: new Date().getFullYear(),
    increment_amount: '',
    reason: '',
    notes: ''
  });

  useEffect(() => {
    if (userId) {
      fetchIncrements();
    }
  }, [userId]);

  const fetchIncrements = async () => {
    setLoading(true);
    try {
      const response = await staffApi.getSalaryIncrements(userId);
      setIncrements(response.data.data.increments || []);
      setTotalIncrement(response.data.data.total_increment || 0);
    } catch (error) {
      toast.error('Failed to fetch salary increments');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedIncrement(null);
    setFormData({
      increment_month: new Date().getMonth() + 1,
      increment_year: new Date().getFullYear(),
      increment_amount: '',
      reason: '',
      notes: ''
    });
    setShowForm(true);
  };

  const handleEdit = (increment) => {
    setSelectedIncrement(increment);
    setFormData({
      increment_month: increment.increment_month,
      increment_year: increment.increment_year,
      increment_amount: increment.increment_amount,
      reason: increment.reason || '',
      notes: increment.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this salary increment?')) {
      return;
    }

    try {
      await staffApi.deleteSalaryIncrement(id);
      toast.success('Salary increment deleted successfully');
      fetchIncrements();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete salary increment');
    }
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Prevent double submission

    
    setSubmitting(true);
    
    try {
      // Validate increment amount
    const incrementAmount = parseFloat(formData.increment_amount);
      if (!formData.increment_amount || isNaN(incrementAmount) || incrementAmount <= 0) {
        toast.error('Please enter a valid increment amount');
        setSubmitting(false);
        return;
      }

      // Validate month and year
      const month = parseInt(formData.increment_month);
      const year = parseInt(formData.increment_year);
      if (!formData.increment_month || !formData.increment_year || isNaN(month) || isNaN(year)) {
        toast.error('Please select month and year');
        setSubmitting(false);
        return;
      }

      // Validate month range
      if (month < 1 || month > 12) {
        toast.error('Month must be between 1 and 12');
        setSubmitting(false);
        return;
      }

      // Validate year range
      if (year < 2000 || year > 2100) {
        toast.error('Year must be between 2000 and 2100');
        setSubmitting(false);
        return;
      }
      // Validate userId before making the request
      if (!userId) {
        toast.error('User ID is missing. Please refresh the page and try again.');
        setSubmitting(false);
        return;
      }

      const userIdNum = parseInt(userId);
      if (isNaN(userIdNum) || userIdNum <= 0) {
        toast.error('Invalid user ID. Please refresh the page and try again.');
        setSubmitting(false);
        return;
      }

      // Prepare data with correct types
      // express-validator accepts both strings and numbers, but we'll send as numbers
      // to match the database schema (int and decimal)
      const submitData = {
        increment_month: month, // integer 1-12
        increment_year: year, // integer 2000-2100
        increment_amount: incrementAmount, // float/decimal
        reason: formData.reason?.trim() || null,
        notes: formData.notes?.trim() || null
      };

      // Double-check data types before sending
      if (typeof submitData.increment_month !== 'number' || 
          typeof submitData.increment_year !== 'number' || 
          typeof submitData.increment_amount !== 'number') {
      
        toast.error('Invalid data format. Please check your input.');
        setSubmitting(false);
        return;
      }

      let response;
      if (selectedIncrement) {
        response = await staffApi.updateSalaryIncrement(selectedIncrement.id, submitData);
        
        // Verify response success
        if (!response?.data?.success) {
          throw new Error(response?.data?.message || 'Failed to update salary increment');
        }
        
        toast.success('Salary increment updated successfully');
      } else {
        try {
          response = await staffApi.createSalaryIncrement(userIdNum, submitData);
          
          // Check for error response even with 200 status
          if (response?.data?.success === false) {
            const errorMsg = response.data.message || 'Failed to create salary increment';
            console.error('API returned success: false', errorMsg);
            throw new Error(errorMsg);
          }
          
          // Verify we got a valid response
          if (!response || !response.data) {
            console.error('Invalid response structure:', response);
            throw new Error('Invalid response from server');
          }
          
          toast.success('Salary increment added successfully');
        } catch (apiError) {
          console.error('API call failed:', apiError);
          console.error('Error details:', {
            message: apiError.message,
            response: apiError.response?.data,
            status: apiError.response?.status,
            statusText: apiError.response?.statusText
          });
          throw apiError; // Re-throw to be caught by outer catch
        }
      }
      
      // Close form and reset
      setShowForm(false);
      setSelectedIncrement(null);
      
      // Refresh increments list
      await fetchIncrements();
      
      // Notify parent to refresh staff data
      if (onIncrementAdded) {
        onIncrementAdded();
      }

      // Close parent modal if provided (only on successful creation, not update)
      if (!selectedIncrement && onCloseModal) {
        onCloseModal();
      }
    } catch (error) {
      console.error('Error creating/updating salary increment:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error config:', error.config);
      
      // Handle different error types
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Validation errors from express-validator
        const errorMessages = error.response.data.errors.map(err => err.message || err.msg).join(', ');
        toast.error(errorMessages);
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error(formatValidationErrors(error));
      }
      // Don't close the form on error so user can fix it
    } finally {
      setSubmitting(false);
    }
  };

  const getMonthName = (month) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[month - 1] || month;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Salary Increment History</h4>
          <p className="text-xs text-gray-500 mt-0.5">Track salary increments for {staffName}</p>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleAdd();
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <HiOutlinePlus className="w-4 h-4" />
          Add Increment
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent"></div>
        </div>
      ) : (
        <>
          {/* Total Increment Summary */}
          {totalIncrement > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-green-900">Total Increment:</span>
                <span className="text-lg font-bold text-green-700">
                  ₹{parseFloat(totalIncrement).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          )}

          {/* Increments List */}
          {increments.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              <p>No salary increments recorded yet</p>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAdd();
                }}
                className="mt-2 text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                Add first increment
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {increments.map((increment) => (
                <div
                  key={increment.id}
                  className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-sm font-semibold text-gray-900">
                          {getMonthName(increment.increment_month)} {increment.increment_year}
                        </span>
                        <span className="text-sm font-bold text-green-600">
                          +₹{parseFloat(increment.increment_amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 space-y-0.5">
                        {increment.previous_salary && (
                          <p>
                            Previous: ₹{parseFloat(increment.previous_salary).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} → 
                            New: ₹{parseFloat(increment.new_salary).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        )}
                        {increment.reason && (
                          <p className="text-gray-600">Reason: {increment.reason}</p>
                        )}
                        {increment.notes && (
                          <p className="text-gray-500 italic">{increment.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-3">
                      <button
                        onClick={() => handleEdit(increment)}
                        className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                        title="Edit"
                      >
                        <HiOutlinePencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(increment.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <HiOutlineTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Increment Form Modal */}
      {showForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowForm(false);
              setSelectedIncrement(null);
            }
          }}
        >
          <div 
            className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {selectedIncrement ? 'Edit Salary Increment' : 'Add Salary Increment'}
              </h3>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowForm(false);
                  setSelectedIncrement(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiX className="w-5 h-5" />
              </button>
            </div>

            <div 
              className="p-4 space-y-4 salary-increment-form"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Month <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="increment_month"
                    value={formData.increment_month}
                    onChange={(e) => setFormData({ ...formData, increment_month: parseInt(e.target.value) })}
                    required
                    className="input w-full text-sm"
                    disabled={!!selectedIncrement}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                      <option key={month} value={month}>
                        {getMonthName(month)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Year <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="increment_year"
                    value={formData.increment_year}
                    onChange={(e) => setFormData({ ...formData, increment_year: parseInt(e.target.value) })}
                    required
                    min="2000"
                    max="2100"
                    className="input w-full text-sm"
                    disabled={!!selectedIncrement}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Increment Amount (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="increment_amount"
                  value={formData.increment_amount}
                  onChange={(e) => setFormData({ ...formData, increment_amount: e.target.value })}
                  required
                  min="0"
                  step="0.01"
                  className="input w-full text-sm"
                  placeholder="Enter increment amount"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Reason
                </label>
                <input
                  type="text"
                  name="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="input w-full text-sm"
                  placeholder="e.g., Performance, Promotion, etc."
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="input w-full text-sm"
                  placeholder="Additional notes..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setSelectedIncrement(null);
                  }}
                  className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSubmit(e);
                  }}
                  disabled={submitting}
                  className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Saving...' : (selectedIncrement ? 'Update' : 'Add') + ' Increment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryIncrements;

